import * as React from "react";
import { cn } from "@/lib/utils";

type TabsContextType = {
  value: string;
  setValue: (v: string) => void;
};

const TabsContext = React.createContext<TabsContextType | null>(null);

export function Tabs({
  defaultValue,
  value: valueProp,
  onValueChange,
  className,
  children,
}: {
  defaultValue?: string;
  value?: string;
  onValueChange?: (v: string) => void;
  className?: string;
  children: React.ReactNode;
}) {
  const [value, setValue] = React.useState(valueProp ?? defaultValue ?? "");
  React.useEffect(() => {
    if (valueProp !== undefined) setValue(valueProp);
  }, [valueProp]);

  const setAndNotify = (v: string) => {
    setValue(v);
    onValueChange?.(v);
  };

  return (
    <div className={cn("w-full", className)}>
      <TabsContext.Provider value={{ value, setValue: setAndNotify }}>
        {children}
      </TabsContext.Provider>
    </div>
  );
}

export function TabsList({ className = "", ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      role="tablist"
      aria-orientation="horizontal"
      onKeyDown={(e) => {
        const container = e.currentTarget;
        const tabs = Array.from(container.querySelectorAll<HTMLButtonElement>('button[role="tab"]'));
        const currentIndex = tabs.findIndex((el) => el === document.activeElement);
        if (tabs.length === 0) return;
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          const next = tabs[(currentIndex + 1 + tabs.length) % tabs.length];
          next?.focus();
          next?.click();
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          const prev = tabs[(currentIndex - 1 + tabs.length) % tabs.length];
          prev?.focus();
          prev?.click();
        } else if (e.key === 'Home') {
          e.preventDefault();
          tabs[0]?.focus();
          tabs[0]?.click();
        } else if (e.key === 'End') {
          e.preventDefault();
          tabs[tabs.length - 1]?.focus();
          tabs[tabs.length - 1]?.click();
        }
      }}
      className={cn("inline-flex items-center gap-2 rounded-lg bg-muted p-1", className)}
      {...props}
    />
  );
}

export function TabsTrigger({
  value,
  className = "",
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { value: string }) {
  const ctx = React.useContext(TabsContext);
  if (!ctx) throw new Error("TabsTrigger must be used within Tabs");
  const active = ctx.value === value;
  return (
    <button
      role="tab"
      aria-selected={active ? 'true' : 'false'}
      id={`tab-${value}`}
      aria-controls={`panel-${value}`}
      tabIndex={active ? 0 : -1}
      type="button"
      onClick={() => ctx.setValue(value)}
      className={cn(
        "px-3 py-1.5 text-sm rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
        active ? "bg-card text-foreground shadow border border-border" : "text-muted-foreground hover:text-foreground",
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
}

export function TabsContent({ value, className = "", ...props }: React.HTMLAttributes<HTMLDivElement> & { value: string }) {
  const ctx = React.useContext(TabsContext);
  if (!ctx) throw new Error("TabsContent must be used within Tabs");
  if (ctx.value !== value) return null;
  return <div role="tabpanel" id={`panel-${value}`} aria-labelledby={`tab-${value}`} className={cn("mt-3", className)} {...props} />;
}
